// Ask Mira BI: a conversation with a data analyst that reads the warehouse
// catalog through tools and runs charts (never SQL) over it.
//
// The page is a thread rail, a transcript and a composer. It owns none of the
// waiting: `useAskThread` follows one conversation (a `bi.ask.settled` push
// over a REST poll) and `useAskChartResult` reads one settled message's rows.
// Everything slow happens in a worker turn, so what arrives here is always
// either a pending row or a finished one. A pending row carries the steps the
// agent has taken so far (pushed as `bi.ask.progress`), so the reader watches
// it work, and a question just sent shows at once, before the server has
// written it (`withSentQuestion`).
//
// `?thread=` is the current conversation, so a reader can link to one. An
// empty `?thread=` is the "new conversation" state: the first question creates
// the thread and the URL follows it.
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Button, Composer, IconButton, useToast } from "@bi/shared";
import type { AskMessageWire } from "@bi/shared/types";

import { ChartCard } from "../components/chart-card";
import { Markdown } from "../components/markdown";
import { ThumbsDownIcon, ThumbsUpIcon } from "../components/icons";
import { EmptyState, errorMessage } from "../components/states";
import { EMPLOYEE_EMAIL } from "../config";
import {
  isAwaitingAnswer,
  type SentQuestion,
  sentQuestionLanded,
  useAskChartResult,
  useAskThread,
  withSentQuestion,
} from "../lib/ask-query";
import {
  askFeedback,
  askFollowUp,
  deleteAskThread,
  fetchAskThreads,
  startAskThread,
} from "../lib/api";
import { dashboardsQuery } from "../lib/queries";

/** A sentence for each failure kind the agent can end a turn with, for the
 *  case where it produced prose but no chart. The prose says it better; this
 *  is the one-word label above it, so a reader scanning a long thread can see
 *  WHICH kind of miss it was. Mirrors `bi/ask/thread.py`'s own vocabulary. */
const FAILURE_LABEL: Record<string, string> = {
  no_source: "No source in the catalog carried that",
  compile_rejected: "The chart builder could not express that",
  dry_run_refused: "That would scan too much of the warehouse",
  proposal_failed: "The chart it tried to build was not valid",
  query_failed: "The warehouse refused to run it",
};

const THREADS_KEY = ["ask", "threads"];

/** First questions offered on an empty conversation. Each is one the agent can
 *  answer from the catalog tools alone or with one cheap chart, so a reader
 *  who does not know what to ask learns what the feature is for. */
const STARTERS = [
  "What data do you have?",
  "What do you have on paid handoffs?",
  "How many projects were created each week over the last 90 days?",
];

/** How close to the bottom (px) still counts as "reading the latest", so a
 *  new step scrolls into view. Further up, the reader is reading history and
 *  the page must not pull them down. */
const FOLLOW_SLACK_PX = 160;

function megabytes(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;
}

export function AskPage({ threadId }: { threadId: string | null }) {
  const dashboards = useQuery(dashboardsQuery);
  const employeeEmail = dashboards.data?.employeeEmail || EMPLOYEE_EMAIL;
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [question, setQuestion] = useState("");
  // The question just sent, shown before the thread read has it. See
  // `withSentQuestion`.
  const [sent, setSent] = useState<SentQuestion | null>(null);
  const threads = useQuery({ queryKey: THREADS_KEY, queryFn: fetchAskThreads });
  const { view, waiting, error: threadError, refresh } = useAskThread(threadId, employeeEmail);

  const openThread = (id: string | null) =>
    void navigate({ to: "/ask", search: { thread: id ?? undefined } });

  // Both ask mutations put the typed question BACK in the composer when they
  // fail. A 409, a signed-out tab or a dropped connection otherwise costs the
  // reader the sentence they just wrote, and there is nowhere to recover it
  // from: the request never reached the transcript.
  // The optimistic bubble goes too: a question the server never took must not
  // sit in the transcript looking asked.
  const restoreOnError = (e: unknown, text: string) => {
    setSent(null);
    setQuestion((current) => (current ? current : text));
    toast.add({ title: errorMessage(e), data: { tone: "danger" } });
  };

  const start = useMutation({
    mutationFn: startAskThread,
    onSuccess: (accepted) => {
      setSent((s) => s && { ...s, threadId: accepted.threadId, seq: accepted.seq });
      void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
      openThread(accepted.threadId);
    },
    onError: (e, body) => restoreOnError(e, body.question),
  });

  const followUp = useMutation({
    mutationFn: (q: string) => askFollowUp(threadId ?? "", { question: q }),
    // The poll parks itself on a settled thread, so the new pending row has to
    // be fetched rather than waited for: without this the only thing that
    // would notice the turn starting is the push, and the poll exists for the
    // case the push does not arrive.
    onSuccess: (accepted) => {
      setSent((s) => s && { ...s, seq: accepted.seq });
      refresh();
      void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
    },
    onError: (e, text) => restoreOnError(e, text),
  });

  // Drop the placeholder rows the moment the read holds the real ones.
  useEffect(() => {
    if (sent && sentQuestionLanded(view, sent)) setSent(null);
  }, [view, sent]);

  const remove = useMutation({
    mutationFn: deleteAskThread,
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
      if (id === threadId) openThread(null);
    },
    onError: (e) => toast.add({ title: errorMessage(e), data: { tone: "danger" } }),
  });

  // Deleting a conversation is the only irreversible thing on this page (the
  // messages cascade away with the thread), so the `x` arms first and deletes
  // on a second click. A two-step button rather than a `confirm()`: a modal
  // dialog blocks the whole page, and this is a rail.
  const [armedDelete, setArmedDelete] = useState<string | null>(null);
  useEffect(() => {
    if (armedDelete === null) return;
    const timer = window.setTimeout(() => setArmedDelete(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [armedDelete]);

  // A sent question counts as busy until the read shows its pending row, so
  // there is no gap between "sending" and "waiting" where a second question
  // could slip in and 409.
  const showSent = sent !== null && sent.threadId === threadId;
  const busy = start.isPending || followUp.isPending || waiting || showSent;
  const messages = showSent ? withSentQuestion(view?.messages ?? [], sent) : (view?.messages ?? []);

  function ask(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setSent({ threadId, seq: null, question: trimmed, sentAt: new Date().toISOString() });
    if (threadId) followUp.mutate(trimmed);
    else start.mutate({ question: trimmed });
  }

  function submit() {
    if (!question.trim() || busy) return;
    const text = question;
    setQuestion("");
    ask(text);
  }

  // Keep the newest message in view as the turn moves, the way any chat does,
  // but only for a reader already at the bottom: one scrolled up to reread an
  // earlier answer is not pulled away from it by every new step. The sentinel
  // is measured against the viewport, so this holds whichever element scrolls.
  const bottom = useRef<HTMLDivElement | null>(null);
  const following = useRef(true);
  useEffect(() => {
    const measure = () => {
      const node = bottom.current;
      if (node)
        following.current =
          node.getBoundingClientRect().top <= window.innerHeight + FOLLOW_SLACK_PX;
    };
    window.addEventListener("scroll", measure, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", measure, { capture: true });
  }, []);
  const lastSteps = messages[messages.length - 1]?.steps?.length ?? 0;
  useEffect(() => {
    if (following.current) bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, lastSteps, waiting]);
  // Sending is an explicit "show me the latest": follow again from here.
  useEffect(() => {
    if (sent) following.current = true;
  }, [sent]);

  return (
    <div className="bi-page bi-ask">
      <aside className="bi-ask-rail">
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          onClick={() => openThread(null)}
          disabled={threadId === null}
        >
          New conversation
        </Button>
        <ul className="bi-ask-rail__list">
          {(threads.data?.threads ?? []).map((thread) => (
            <li
              key={thread.id}
              className={
                thread.id === threadId ? "bi-ask-rail__item is-active" : "bi-ask-rail__item"
              }
            >
              <button
                type="button"
                className="bi-ask-rail__open"
                onClick={() => openThread(thread.id)}
                title={thread.title}
              >
                {thread.title || "Untitled"}
              </button>
              <button
                type="button"
                className={
                  armedDelete === thread.id ? "bi-ask-rail__delete is-armed" : "bi-ask-rail__delete"
                }
                aria-label={
                  armedDelete === thread.id
                    ? `Confirm deleting "${thread.title}"`
                    : `Delete "${thread.title}"`
                }
                title={
                  armedDelete === thread.id
                    ? "Click again to delete this conversation"
                    : "Delete this conversation"
                }
                onClick={() => {
                  if (armedDelete === thread.id) {
                    setArmedDelete(null);
                    remove.mutate(thread.id);
                  } else {
                    setArmedDelete(thread.id);
                  }
                }}
              >
                {armedDelete === thread.id ? "Sure?" : "x"}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="bi-ask-main">
        {threadId === null && !showSent ? (
          <div className="bi-page__head">
            <h1 className="bi-page__title">Ask</h1>
            <p className="bi-page__sub">
              Ask about Mira's warehouse in plain language, and keep asking: this is a conversation,
              so a follow-up like "now break that down by arm" works. It can also tell you what data
              exists at all, which is often the better first question. It runs real queries over one
              source at a time, with an aggregate, a group by, a time window and simple filters, and
              it will get some things wrong. Your thumbs on each answer decide what gets built next.
            </p>
            <div className="bi-ask-starters" role="group" aria-label="Example questions">
              {STARTERS.map((starter) => (
                <Button
                  key={starter}
                  variant="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => ask(starter)}
                  disabled={busy}
                >
                  {starter}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {threadError ? (
          <div className="bi-panel">
            <div className="bi-panel__body">
              <p className="bi-ask-error">{threadError}</p>
            </div>
          </div>
        ) : null}

        {threadId !== null && view === null && !threadError && !showSent ? (
          <EmptyState title="Loading this conversation…" />
        ) : null}

        <div className="bi-ask-thread">
          {messages.map((message) =>
            message.role === "user" ? (
              <UserMessage key={message.seq} message={message} />
            ) : (
              <AssistantMessage
                key={message.seq}
                message={message}
                question={questionFor(messages, message.seq)}
                onAskAgain={busy ? undefined : ask}
              />
            ),
          )}
          <div ref={bottom} />
        </div>

        <div className="bi-panel bi-ask-dock">
          <div className="bi-panel__body">
            <div className="bi-ask-composer">
              <Composer
                value={question}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
                onSubmit={submit}
                submitOnEnter
                minRows={1}
                placeholder={
                  threadId
                    ? "Ask a follow-up, or refine the last chart…"
                    : "What data do you have on paid handoffs?"
                }
                aria-describedby={busy ? "bi-ask-busy-note" : undefined}
              />
              <Button
                variant="solid"
                tone="accent"
                size="md"
                onClick={submit}
                disabled={busy || !question.trim()}
              >
                {busy ? "Working…" : "Ask"}
              </Button>
            </div>
            {/* The composer stays open while a turn runs, so the reader can
                write the next question; only sending waits. Saying so is
                what keeps a disabled Ask button from reading as broken. */}
            {busy ? (
              <p id="bi-ask-busy-note" className="bi-panel__note bi-ask-composer__note">
                Write your next question while you wait. You can send it when this answer is done.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The question an assistant message answers: the user row immediately before
 *  it. Needed for the feedback row, which is keyed by question text rather
 *  than by message id — `bi_ask_feedback` predates threads and stays a census
 *  of questions rather than a second copy of the transcript. */
function questionFor(messages: AskMessageWire[], seq: number): string {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.seq < seq && m.role === "user") return m.text;
  }
  return "";
}

function UserMessage({ message }: { message: AskMessageWire }) {
  return (
    <div className="bi-ask-turn bi-ask-turn--user">
      <p className="bi-ask-turn__text">{message.text}</p>
    </div>
  );
}

function AssistantMessage({
  message,
  question,
  onAskAgain,
}: {
  message: AskMessageWire;
  question: string;
  /** Re-send `question` as a new turn. Absent while another turn is busy. */
  onAskAgain?: (question: string) => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [feedbackGiven, setFeedbackGiven] = useState<"up" | "down" | null>(null);
  const [attempt, setAttempt] = useState(0);
  const chartState = useAskChartResult(message.resultKey || null, attempt);

  const chart = message.chart;
  const feedback = useMutation({ mutationFn: askFeedback });

  const emptyResult = chartState.status === "ready" && chartState.result.row_count === 0;
  const spec = useMemo(() => (chart ? JSON.stringify(chart, null, 2) : ""), [chart]);

  function copyAnswer() {
    // Clipboard access can be refused (an insecure origin, a denied
    // permission); say so rather than claim a copy that did not happen.
    navigator.clipboard.writeText(message.text).then(
      () => toast.add({ title: "Answer copied." }),
      () => toast.add({ title: "Could not copy the answer.", data: { tone: "danger" } }),
    );
  }

  function sendFeedback(verdict: "up" | "down") {
    if (feedbackGiven) return;
    setFeedbackGiven(verdict);
    feedback.mutate({
      question,
      verdict,
      sourceId: message.sourceId ?? undefined,
      spec: chart ?? undefined,
      failureKind: verdict === "down" ? (emptyResult ? "no_rows" : "wrong_answer") : undefined,
    });
    toast.add({ title: "Thanks, that's recorded." });
  }

  if (message.status === "pending") {
    // A pending row past the server's staleness window is a turn that was
    // lost, not one still running: nothing will ever settle it, so rendering
    // "working out an answer" is a lie that renews itself on every visit.
    if (!isAwaitingAnswer(message)) {
      return (
        <div className="bi-ask-turn bi-ask-turn--agent">
          <p className="bi-ask-error">This question never got an answer.</p>
          <p className="bi-panel__note">
            The turn was lost before it ran. Ask it again, and if it keeps happening, tell whoever
            runs this environment.
          </p>
          <AskAgain question={question} onAskAgain={onAskAgain} />
        </div>
      );
    }
    return (
      <div className="bi-ask-turn bi-ask-turn--agent">
        <LiveProgress message={message} />
      </div>
    );
  }

  if (message.status === "error") {
    return (
      <div className="bi-ask-turn bi-ask-turn--agent">
        <p className="bi-ask-error">That question could not be answered.</p>
        {message.error ? <p className="bi-panel__note">{message.error}</p> : null}
        <Steps message={message} />
        <AskAgain question={question} onAskAgain={onAskAgain} />
      </div>
    );
  }

  return (
    <div className="bi-ask-turn bi-ask-turn--agent">
      {message.failureKind ? (
        <p className="bi-ask-turn__label">{FAILURE_LABEL[message.failureKind] ?? "No chart"}</p>
      ) : null}
      <Markdown source={message.text} className="bi-ask-turn__text" />

      {chart ? (
        <div className="bi-ask-chart">
          <div className="bi-ask-chart__head">
            {message.bytesEstimate != null ? (
              <p className="bi-panel__note">Scanned about {megabytes(message.bytesEstimate)}.</p>
            ) : (
              <span />
            )}
            <Button
              variant="outline"
              tone="neutral"
              size="sm"
              onClick={() =>
                void navigate({
                  to: "/studio/new",
                  search: { source: message.sourceId ?? undefined },
                })
              }
            >
              Open in the studio
            </Button>
          </div>
          <ChartCard
            chart={chart}
            state={chartState}
            columnSpan={12}
            sizeClass="lg"
            inactiveFilterCount={0}
            authoredConstraints={[]}
            ignoresDateRange={false}
            dataset={undefined}
            onRetry={() => setAttempt((n) => n + 1)}
          />
          <details className="bi-ask-spec">
            <summary>The spec that produced this chart</summary>
            <pre>{spec}</pre>
          </details>
        </div>
      ) : null}

      <Steps message={message} />

      <div className="bi-ask-thumbs">
        <span className="bi-field__label">Was this a good answer?</span>
        <IconButton
          variant={feedbackGiven === "up" ? "solid" : "ghost"}
          tone={feedbackGiven === "up" ? "success" : "neutral"}
          size="sm"
          aria-label="Good answer"
          title="Good answer"
          disabled={feedbackGiven !== null}
          onClick={() => sendFeedback("up")}
        >
          <ThumbsUpIcon size={16} />
        </IconButton>
        <IconButton
          variant={feedbackGiven === "down" ? "solid" : "ghost"}
          tone={feedbackGiven === "down" ? "danger" : "neutral"}
          size="sm"
          aria-label="Not a good answer"
          title="Not a good answer"
          disabled={feedbackGiven !== null}
          onClick={() => sendFeedback("down")}
        >
          <ThumbsDownIcon size={16} />
        </IconButton>
        <Button variant="ghost" tone="neutral" size="sm" onClick={copyAnswer}>
          Copy answer
        </Button>
      </div>
    </div>
  );
}

/** "Ask again" under an answer that failed: the same question as a new turn,
 *  without retyping it. Hidden while another turn is busy (the thread takes
 *  one at a time) and when there is no question to re-send. */
function AskAgain({
  question,
  onAskAgain,
}: {
  question: string;
  onAskAgain?: (question: string) => void;
}) {
  if (!onAskAgain || !question) return null;
  return (
    <div className="bi-ask-again">
      <Button variant="outline" tone="neutral" size="sm" onClick={() => onAskAgain(question)}>
        Ask again
      </Button>
    </div>
  );
}

/** Seconds since `since`, ticking once a second while mounted. */
function useElapsedSeconds(since: string): number {
  const started = Date.parse(since);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  if (Number.isNaN(started)) return 0;
  // Clamped: the placeholder row starts on this tab's clock and the real row
  // on the server's, and a skewed clock must not show a negative count.
  return Math.max(0, Math.floor((now - started) / 1_000));
}

/** A turn in flight, as the reader watches it: every step the agent has
 *  finished, the one it is on now, and how long it has been going.
 *
 *  The steps come from the pending row itself (the worker writes them as it
 *  goes and pushes `bi.ask.progress`), so this is the same list the settled
 *  answer shows under "N steps", seen while it is being made. When no step is
 *  running the agent is between tool calls, which on this model means it is
 *  reasoning, so the tail says "Thinking". The elapsed count is there because
 *  a turn at high reasoning can take a minute, and a number that moves is
 *  what tells a reader it has not hung. */
function LiveProgress({ message }: { message: AskMessageWire }) {
  const elapsed = useElapsedSeconds(message.createdAt);
  const steps = message.steps ?? [];
  const running = steps.find((step) => step.running);
  const current = running ? running.summary : steps.length ? "Thinking" : "Reading your question";
  return (
    <div className="bi-ask-live">
      {steps.length > 0 ? (
        <ol className="bi-ask-live__steps">
          {steps
            .filter((step) => !step.running)
            .map((step, i) => (
              <li key={i} className={step.ok ? "bi-ask-live__step" : "bi-ask-live__step is-failed"}>
                <span className="bi-ask-live__mark" aria-hidden="true">
                  {step.ok ? "✓" : "✗"}
                </span>
                {step.summary}
              </li>
            ))}
        </ol>
      ) : null}
      <p className="bi-ask-live__current" role="status" aria-live="polite">
        <span className="bi-ask-live__dot" aria-hidden="true" />
        <span>{current}…</span>
        <span className="bi-ask-live__elapsed">{elapsed}s</span>
      </p>
    </div>
  );
}

/** What the agent went and looked at, collapsed.
 *
 *  Worth showing at all because this feature's whole credibility is whether a
 *  reader can tell an answer from a guess: a paragraph that says "1,240
 *  projects" reads identically whether a query produced it or a model did, and
 *  the step list is the difference. Collapsed because most of the time the
 *  answer is the point. */
function Steps({ message }: { message: AskMessageWire }) {
  const steps = message.steps ?? [];
  if (steps.length === 0) return null;
  return (
    <details className="bi-ask-steps">
      <summary>{steps.length === 1 ? "1 step" : `${steps.length} steps`}</summary>
      <ol>
        {steps.map((step, i) => (
          <li key={i} className={step.ok ? undefined : "is-failed"}>
            {step.summary}
          </li>
        ))}
      </ol>
    </details>
  );
}
